<template>
  <section class="page--section">
    <div class="section__title">
      <el-row :gutter="10" type="flex" align="middle">
        <el-col :span="18">
          <h2>{{ $t('trainingAvailableTraining') }}</h2>
        </el-col>
      </el-row>
    </div>

    <el-row class="new-user-intro" type="flex" v-if="!currentUser.talentLmsUserId">
      <el-col :span="24" class="new-user-intro__content" v-loading="isLoading">
        <div v-html="trainingLanding.newUserIntro"></div>
        <el-button v-on:click="activateAccountClick" class="new-user-intro__button" type="primary">{{
          $t('trainingEnrollCta')
        }}</el-button>
      </el-col>
    </el-row>

    <el-row class="new-user-intro" type="flex" v-if="justEnrolled">
      <el-col :span="24" class="new-user-intro__content">
        <div v-html="trainingLanding.trainingEnrolledMessage"></div>
      </el-col>
    </el-row>

    <el-row :gutter="10">
      <el-col :md="6" :xs="24" :sm="10">
        <el-tree class="course-filter" :data="categoryTree" :indent="0" node-key="id" :default-expand-all="true"
          ref="tree" :highlight-current="true" @node-click="handleCategoryClick">
        </el-tree>
      </el-col>
      <el-col :md="18" :xs="24" :sm="14">
        <div v-for="course in filteredCourses" :key="course.id">

          <training-in-progress-catalog-card v-if="courseInProgress(course)" :course="course"
            :usersCourse="usersCourseForCourse(course)">
          </training-in-progress-catalog-card>

          <training-completed-card v-else-if="courseCompleted(course)" :course="course"
            :usersCourse="usersCourseForCourse(course)">
          </training-completed-card>

          <training-available-card v-else :course="course" :locked="!currentUser.talentLmsUserId">
          </training-available-card>

        </div>
      </el-col>
    </el-row>
  </section>
</template>

<script>
import api from '@/api';
import user from '@/components/user';
import training from '@/components/training';
import TrainingAvailableCard from '@/components/training/TrainingAvailableCard.vue';
import TrainingInProgressCatalogCard from '@/components/training/TrainingInProgressCatalogCard.vue';
import TrainingCompletedCard from '@/components/training/TrainingCompletedCard.vue';
import { mapActions, mapGetters, mapState } from 'vuex';

export default {
  name: 'TrainingAvailable',
  props: {
    availableCourses: Array,
    usersCourses: Array,
  },
  computed: {
    ...mapState(user.store.namespace, [
      'currentUser',
    ]),
    ...mapState(training.store.namespace, [
      'trainingLanding',
    ]),
    defaultExpanded() {
      console.info([this.firstCategoryInTree(this.categoryTree, false)]);
      return [this.firstCategoryInTree(this.categoryTree, false)];
    },
    filteredCourses() {
      return (this.availableCourses || []).filter((course) => this.displayCourse(course));
    }
  },
  data() {
    return {
      categoryTree: buildCategoryTree(this.availableCourses),
      selectedCategory: String,
      isLoading: false,
      justEnrolled: false,
    }
  },
  components: {
    TrainingAvailableCard,
    TrainingInProgressCatalogCard,
    TrainingCompletedCard,
  },
  methods: {
    ...mapActions(user.store.namespace, [
      'setTalentLmsUserId',
    ]),
    usersCourseForCourse: function (course) {
      // return the user's course info for a specific catalog course.
      return this.usersCourses.find(userCourse => userCourse.id === course.id);
    },
    courseCompleted: function (course) {
      // check if a course has been assigned to the user and see if the user completed it.
      let usersCourse = this.usersCourseForCourse(course);
      if (!usersCourse) {
        // The user has not started the course.
        return false;
      }

      return usersCourse.status === 'completed';
    },
    courseInProgress: function (course) {
      // check if a course has been assigned to the user and see if the user completed it.
      let usersCourse = this.usersCourseForCourse(course);
      if (!usersCourse) {
        // The user has not started the course.
        return false;
      }

      return usersCourse.status === 'inProgress';
    },
    activateAccountClick: function (event) {
      this.isLoading = true;
      api.get('actions/mercury/talent-lms/create-account')
        .then(response => {
          this.setTalentLmsUserId(response.talentLmsUserId);
          this.isLoading = false;
          this.justEnrolled = true;
        });
    },
    handleCategoryClick(data) {
      if (data.hasOwnProperty('children') && data.children.length) {
        // Select the first child.
        this.selectedCategory = data.children[0].label;
        this.$refs.tree.setCurrentKey(data.label + '.' + data.children[0].label);
      }
      else {
        // Select the clicked category.
        this.selectedCategory = data.label;
        this.$refs.tree.setCurrentKey(data.label);
      }
    },
    buildCategoryTree,
    firstCategoryInTree,
    displayCourse,
  },
  mounted() {
    this.selectedCategory = firstCategoryInTree(this.categoryTree, false);
    let firstPath = firstCategoryInTree(this.categoryTree, true);
    this.$refs.tree.setCurrentKey(firstPath);
  },
}

/**
 * Derive the list of distinct categories from the set of available courses's categories.
 * This includes creating categories for the full line certifications
 * The category tree is built in the way expected by Element UI's tree component.
 */
function buildCategoryTree(courses) {
  let categoryTree = [];

  // Loop through the available courses and build a category tree.
  courses.forEach((course) => {
    if (course.mainCategory) {
      addCategoryToTree(course.mainCategory, course.subcategory, categoryTree);
    }

    if (course.fullLine) {
      addCategoryToTree('Certificates', course.fullLine, categoryTree);
    }
  });

  function categorySort(a, b) {
    // Sort "Outboards" category first
    if (a.id === 'Outboards') {
      return -1;
    }
    if (b.id === 'Outboards') {
      return 1;
    }
    let collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    return collator.compare(a.label, b.label);
  }

  categoryTree.sort(categorySort);

  let certificatesNode = categoryTree.find((node) => node.id === 'Certificates');
  if (certificatesNode) {
    function certificatesSort(a, b) {
      // Sort "Outboard Product Expert" category first
      if (a.id === 'Certificates.Outboard Product Expert') {
        return -1;
      }
      if (b.id === 'Certificates.Outboard Product Expert') {
        return 1;
      }
      let collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
      return collator.compare(a.label, b.label);
    }
    certificatesNode.children.sort(certificatesSort);
  }


  return categoryTree;
}


/**
 * Add a category to a category tree, making sure not to duplicate entries.
 */
function addCategoryToTree(categoryToFind, subcategoryToFind, tree) {

  let foundCategory = tree.filter(category => (category.label === categoryToFind));

  if (!foundCategory.length) {
    // The category does not already exist in the tree, so add it.
    let children = [];

    if (subcategoryToFind) {
      children = [{
        label: subcategoryToFind,
        id: categoryToFind + '.' + subcategoryToFind,
      }];
    }

    tree.push({
      label: categoryToFind,
      id: categoryToFind,
      children: children,
    });

    return;
  }

  // The current course's category has already been stored in the tree.
  if (!subcategoryToFind) {
    // There is no subcategory to worry about, get out of here.
    return;
  }

  // Let's see if its subcategory is in the tree.
  let foundSubcategory = foundCategory[0].children.filter(category => (category.label === subcategoryToFind));

  if (!foundSubcategory.length) {
    // The subcategory does not already exist for the category, so add it.
    foundCategory[0].children.push({
      label: subcategoryToFind,
      id: categoryToFind + '.' + subcategoryToFind,
    })
  }
}


/**
 * Determine if a course should be displayed based on the current filter.
 */
function displayCourse(course) {
  if (course.fullLine === this.selectedCategory) {
    return true;
  }

  if (course.subcategory) {
    if (course.subcategory === this.selectedCategory) {
      return true;
    }
    return false;
  }

  if (course.mainCategory === this.selectedCategory) {
    return true;
  }
  return false;
}


/**
 * Return the first value in a tree. Either the first category
 * or the first subcategory. If return path is set to True, this
 * function will return the full path to the first category.
 */
function firstCategoryInTree(tree, returnPath) {
  if (!tree || !tree.length) {
    return null;
  }

  let first = tree[0]

  if (first.children.length) {
    if (returnPath) {
      return first.label + '.' + first.children[0].label;
    }
    return first.children[0].label;
  }

  return first.label;
}

</script>

<style lang="scss">
@import '@/assets/base/_variables.scss';
@import 'element-theme-chalk/src/mixins/mixins.scss';

.course-filter {
  background-color: #F9F9F9;
  color: black;
  font-family: $font-family;
  margin-right: 20px;

  @include res(xs) {
    margin: 0 0 30px 0;
  }

}

.course-filter .el-tree-node__label {
  font-size: 15px !important;
}

.el-tree-node .is-current>.el-tree-node__content {
  background-color: #F2F2F2 !important;
}

.course-filter>.is-expanded>.el-tree-node__content {
  background-color: transparent !important;
  /*color: #D31F25;*/
}

.course-filter {

  &>.is-expanded>.el-tree-node__content,
  &>.is-current>.el-tree-node__content,
  &>.is-focusable>.el-tree-node__content {
    background-color: #F9F9F9;
    text-transform: uppercase;
    font-weight: bold;
  }

  .el-tree-node__expand-icon.el-icon-caret-right {
    position: absolute;
    right: 10px;
  }

  .el-tree-node__expand-icon {
    transform: rotate(180deg) !important;
  }

  .el-icon-caret-right:before {
    content: "\E6DE";

    font-weight: bold;
  }

  .el-tree-node__expand-icon.expanded {
    transform: rotate(270deg) !important;
  }

  .el-tree-node__expand-icon:not(.is-leaf) {
    color: #D31F25;
  }

}

.el-tree--highlight-current .el-tree-node.is-current>.el-tree-node__content {
  background-color: transparent !important;
}

.el-tree--highlight-current .el-tree-node.is-current.is-expanded>.el-tree-node__content {
  color: #D31F25;
}

.el-tree-node__content {
  padding: 18px 0 16px 25px !important;
  position: relative;
}

.card-horizontal__link-emphasized::after,
.card-horizontal .card-horizontal__link-locked::after {
  content: "\E6DE";
  font-family: element-icons !important;
  display: inline-block;
  -webkit-transform: rotate(180deg);
  transform: rotate(180deg);
  top: -1px;
  margin-left: 3px;
  position: relative;
}


.course-filter .el-tree-node__children {
  background-color: #F4F4F4;

  .el-tree-node__content {
    padding: 12px 0;
  }

  .el-icon-caret-right {
    display: none;
  }
}

.course-filter .el-tree-node__children .is-current {
  background-color: #F0F0F0 !important;
  font-weight: bold;
}

.course-filter .el-tree-node__children .is-current .el-tree-node__content {
  box-shadow: inset 4px 0px 0px 0px rgba(211, 31, 37, 1);

}
</style>

<style lang="scss" scoped>
.new-user-intro {

  &__content {
    background: #F9F9F9;
    padding: 30px 60px;
    margin-bottom: 20px;
  }

  &__button {
    text-align: center;
    margin-top: 20px;
  }
}
</style>